import type { z } from 'zod';

/**
 * The contract one integration declares once and every surface reads from
 * (docs/plan/form-integration-elements/plan.md § 2). The descriptor is data
 * plus Zod: the builder configures it, the engine validates against it, the
 * hosted form runs its actions through one generic procedure, and the claim
 * pipeline applies its effects. Behaviour lives in the server and client
 * modules that share its `key`; nothing about an integration is authored in
 * form JSON beyond `integrationKey`, `config` and the presentation.
 */

/** Where an integration's live UI may run. The review page and submissions drawer always render its summary. */
export type IntegrationSurface = 'hosted' | 'console';

/** How the element sits on its page: among other fields, or owning the page and its Next control. */
export type IntegrationPresentation = 'inline' | 'page';

/** The scalar types an output may carry, mirrored onto the element types mappings understand. */
export type IntegrationOutputType = 'text' | 'number' | 'boolean' | 'date';

export type IntegrationOutputField = {
    type: IntegrationOutputType;
    label: string;
    /** Offered as a mapping source (contact, claim and custom fields). */
    mappable?: boolean;
    /** Masked on the review page and in the submissions drawer, and never sent to the claimant's browser. */
    sensitive?: boolean;
    /**
     * Defaults to true. False keeps an output the claimant's own step needs (a one-time link)
     * out of the read-back summaries: the review page, the submissions drawer and text answers.
     */
    summary?: boolean;
};

/** A control of the generic, schema-driven config editor. */
export type IntegrationConfigField =
    | { key: string; label: string; control: 'switch'; help?: string }
    | { key: string; label: string; control: 'text'; help?: string; placeholder?: string }
    | { key: string; label: string; control: 'select'; help?: string; options: { value: string; label: string }[] }
    | {
          key: string;
          label: string;
          control: 'multiselect';
          help?: string;
          options: { value: string; label: string }[];
      }
    | {
          key: string;
          label: string;
          /** Picks a sibling element of the form by storage key; its answer is read server-side. */
          control: 'element';
          help?: string;
          elementTypes: readonly string[];
      };

export type IntegrationActionSpec = {
    input: z.ZodType;
    output: z.ZodType;
    /** Per-draft budget: calls per window. The procedure adds its own per-IP limiter on top. */
    limit: { max: number; windowMs: number };
    /**
     * Whether the action calls the provider. Defaults to true, which requires the
     * org's integration to be available. Local draft operations (skip, read the
     * stored status, reset) set false so a claimant can still finish a form after
     * the organisation switches the provider off.
     */
    requiresProvider?: boolean;
};

/** What the claim pipeline does with a completed element; the server module implements each. */
export type IntegrationClaimEffect =
    | 'linkFleetVehicle'
    | 'attachVehicleInspection'
    | 'attachFiles'
    | 'linkSupplier'
    | 'recordVehicleIdentity';

/** The reserved output every integration value carries. */
export const INTEGRATION_STATUS_KEY = 'status';
/** Opaque server material (ids, tokens). Never mappable, never shown, never in expressions. */
export const INTEGRATION_PROOF_KEY = '_proof';

export type IntegrationDescriptor<
    TConfig extends z.ZodType = z.ZodType,
    TActions extends Record<string, IntegrationActionSpec> = Record<string, IntegrationActionSpec>,
> = {
    key: string;
    /** Stored with every value; the contract diff treats a change as a change of value type. */
    version: number;
    label: string;
    description: string;
    /** A Lucide icon name the client resolves; a plain signifier, not a brand. */
    icon: 'car' | 'scan' | 'search' | 'plug';
    surfaces: readonly IntegrationSurface[];
    presentation: IntegrationPresentation;
    configSchema: TConfig;
    configFields: readonly IntegrationConfigField[];
    outputs: Record<string, IntegrationOutputField>;
    completion: {
        /** What "answered" means for this element; unanswered plus `blocking` is a validation error. */
        answered: (value: Record<string, unknown>) => boolean;
        /** Next stays disabled and submit refuses until answered. */
        blocking: boolean;
    };
    actions: TActions;
    claimEffects: readonly IntegrationClaimEffect[];
    /**
     * Config keys whose values name sibling elements this integration reads (a
     * repairer search around the incident address). The action runner hands the
     * named elements' current draft answers to `run`, so the server, not the
     * client, says what the sibling answered.
     */
    readsAnswers?: readonly string[];
};

export type IntegrationConfigOf<D extends IntegrationDescriptor> = z.infer<D['configSchema']>;

/** The element's value: the reserved status, the declared outputs, and the opaque proof. */
export type IntegrationValue = { [INTEGRATION_STATUS_KEY]: string } & Record<string, unknown>;

/** Sensible narrowing for a stored value: an object whose status is a string. */
export function isIntegrationValue(value: unknown): value is IntegrationValue {
    return (
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value) &&
        typeof (value as Record<string, unknown>)[INTEGRATION_STATUS_KEY] === 'string'
    );
}

/** The outputs an author may map or reference: everything declared, minus the proof. */
export function integrationOutputEntries(descriptor: IntegrationDescriptor): [string, IntegrationOutputField][] {
    return Object.entries(descriptor.outputs).filter(([key]) => key !== INTEGRATION_PROOF_KEY);
}

/**
 * The value with its sensitive outputs removed but its proof kept — what may leave the
 * server for the claimant's browser. The proof is opaque and the hosted UI needs it (a
 * chosen repairer, a scan id); sensitive outputs are for the claim, never for a screen.
 */
export function claimantIntegrationValue(descriptor: IntegrationDescriptor, value: IntegrationValue): IntegrationValue {
    const shown = { ...value };
    for (const [key, output] of Object.entries(descriptor.outputs)) {
        if (output.sensitive) delete shown[key];
    }
    return shown;
}

/**
 * The value with its proof, its sensitive outputs and its `summary: false` outputs removed —
 * what any read-back UI may show.
 */
export function presentableIntegrationValue(
    descriptor: IntegrationDescriptor,
    value: IntegrationValue,
): Record<string, unknown> {
    // Descriptor order, not storage order: JSONB reorders keys, and a summary
    // should read registration before colour whatever the database did.
    const shown: Record<string, unknown> = {};
    const keys = [
        ...Object.keys(descriptor.outputs),
        ...Object.keys(value).filter((key) => !(key in descriptor.outputs)),
    ];
    for (const key of keys) {
        if (key === INTEGRATION_PROOF_KEY || !(key in value)) continue;
        const output = descriptor.outputs[key];
        if (output?.sensitive || output?.summary === false) continue;
        shown[key] = value[key];
    }
    return shown;
}

/** The step word for an answered value whose status has no shared meaning of its own. */
export const INTEGRATION_STEP_COMPLETE = 'complete';

/**
 * Statuses whose meaning every descriptor shares: the step ended, but not well. They count as
 * answered (the claimant may move on) without being a success, so they keep their own word
 * instead of collapsing into `complete`.
 */
const TERMINAL_NON_SUCCESS: ReadonlySet<string> = new Set(['failed', 'skipped', 'unverified']);

/**
 * Labels for the step words the badge and text summaries show. A descriptor's other in-flight
 * statuses render as their own word.
 */
export const INTEGRATION_STEP_LABELS: Readonly<Record<string, string>> = {
    [INTEGRATION_STEP_COMPLETE]: 'Completed',
    failed: 'Failed',
    skipped: 'Skipped',
    unverified: 'Unverified',
    idle: 'Not started',
    challenged: 'Awaiting code',
    requested: 'Link sent',
    found: 'Awaiting confirmation',
};

/**
 * The step an integration value is at, as one word: `failed` or `skipped` when that is the
 * status, `complete` for any other answered value, otherwise the descriptor's in-flight word.
 * "Answered" (may proceed) and "succeeded" are different predicates; this keeps them apart.
 */
export function integrationStepStatus(descriptor: IntegrationDescriptor, value: IntegrationValue): string {
    const status = String(value[INTEGRATION_STATUS_KEY]);
    if (TERMINAL_NON_SUCCESS.has(status)) return status;
    return descriptor.completion.answered(value) ? INTEGRATION_STEP_COMPLETE : status;
}

export function integrationStepLabel(step: string): string {
    return INTEGRATION_STEP_LABELS[step] ?? step;
}

/**
 * One line for an integration value wherever answers are read as text: the
 * step word, then up to four shown outputs as "Label: value".
 */
export function describeIntegrationValue(descriptor: IntegrationDescriptor, value: unknown): string {
    if (!isIntegrationValue(value)) return 'Not completed';
    const head = integrationStepLabel(integrationStepStatus(descriptor, value));
    const parts = Object.entries(presentableIntegrationValue(descriptor, value))
        .filter(([key, raw]) => key !== INTEGRATION_STATUS_KEY && raw !== null && raw !== undefined && raw !== '')
        .slice(0, 4)
        .map(([key, raw]) => `${descriptor.outputs[key]?.label ?? key}: ${String(raw)}`);
    return parts.length > 0 ? `${head} · ${parts.join(' · ')}` : head;
}
